import { useTranslation } from 'react-i18next';
import { submitFoutSleutel, type SubmitProbleem } from '../submit-foutmelding';

/** Meer dan dit leest niemand meer; de rest wordt geteld. */
const HOOGSTENS_GETOOND = 10;

const AFGEKEURD = 'intake.review.submitBar.errorFileRejected';

/**
 * h6-3 — wat er van een mislukte indienpoging op het scherm blijft staan.
 *
 * Zonder dit komt een klant die het tabblad sloot terug op een schoon reviewscherm, zonder enig
 * teken dat er al iets bij CustomerPortal staat. Hij drukt dan opnieuw op indienen zonder te weten
 * dat hij een vorige poging hervat — en als er iets misgaat, weet hij ook niet waaróm het de vorige
 * keer misging.
 *
 * Staat er al een ordernummer bij, dan is dat het belangrijkste wat hij moet weten: het concept
 * bestaat, en opnieuw proberen maakt er geen tweede.
 *
 * H13-9 — de meldingen per regel horen hieronder. De tekst bij een afgekeurd bestand verwees er al
 * naar terwijl ze nergens stonden; ze leven alleen zolang het scherm de mislukte poging vasthoudt,
 * dus na herladen zegt de tekst ook niet meer dat ze er zijn.
 */
export function SubmitErrorBanner({
  errorCode,
  orderNumber,
  problems = [],
}: {
  errorCode: string | null;
  orderNumber: string | null;
  problems?: SubmitProbleem[];
}) {
  const { t, i18n } = useTranslation();
  if (!errorCode) return null;

  // Een sleutel zonder vertaling overslaan; die regel is voor de klant toch onleesbaar, en een rauwe
  // sleutel op het scherm is erger dan een melding minder. Datzelfde geldt voor de veldnaam, want
  // die wordt ín de tekst ingevuld.
  //
  // Eerst afkappen, dán vertalen: een bestand kan duizenden meldingen opleveren, en die allemaal
  // vertalen bij elke render is werk waarvan er tien op het scherm belanden.
  const regels = problems
    .filter((probleem) => i18n.exists(probleem.key) && (probleem.field == null || i18n.exists(veldSleutel(probleem.field))))
    .slice(0, HOOGSTENS_GETOOND)
    .map((probleem) =>
      t(probleem.key, {
        row: probleem.row,
        value: probleem.value,
        shown: probleem.shown,
        ...(probleem.field ? { field: t(veldSleutel(probleem.field)) } : {}),
      }),
    );

  // Tellen vanaf wat er binnenkwam, niet vanaf wat er te tonen was. Een melding die hier wegviel is
  // er voor de klant nog steeds één: herstelt hij alleen wat hij ziet, dan wordt hij opnieuw
  // afgekeurd zonder te snappen waarom.
  const nietGetoond = problems.length - regels.length;

  return (
    <div
      role="status"
      data-testid="intake-submit-error-banner"
      className="rounded-[var(--radius-card)] border border-[var(--warning-border)] bg-[var(--warning-background)] p-3 text-sm"
    >
      <p>{t(meldingSleutel(submitFoutSleutel({ response: { data: { error: { code: errorCode } } } }), problems.length, regels.length))}</p>
      {orderNumber ? (
        <p data-testid="intake-submit-error-order" className="mt-1 text-[var(--foreground-muted)]">
          {t('intake.review.submitBar.previousAttemptOrder', { orderNumber })}
        </p>
      ) : null}
      {regels.length > 0 ? (
        <div data-testid="intake-submit-error-problems" className="mt-2">
          <p className="font-medium">{t('intake.review.submitBar.problemsHeading')}</p>
          <ul className="mt-1 list-disc pl-5">
            {regels.map((regel, index) => (
              <li key={index}>{regel}</li>
            ))}
          </ul>
          {nietGetoond > 0 ? (
            <p data-testid="intake-submit-error-problems-more" className="mt-1 text-[var(--foreground-muted)]">
              {t('intake.review.submitBar.problemsMore', { count: nietGetoond })}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Welke tekst boven de lijst hoort. Alleen het afgekeurde bestand verwijst naar meldingen per
 * regel, dus alleen daar kan die verwijzing loos worden.
 */
function meldingSleutel(sleutel: string, aangeleverd: number, getoond: number): string {
  if (sleutel !== AFGEKEURD) return sleutel;
  // Er kwam geen lijst mee — na herladen staat alleen de foutcode nog op de sessie. Opnieuw
  // indienen laat de meldingen wél zien, dus dát is het advies.
  if (aangeleverd === 0) return 'intake.review.submitBar.errorFileRejectedNoDetails';
  // Er kwam wél een lijst, maar er was niets van te tonen. "Probeer het opnieuw, dan ziet u de
  // regels" zou hier liegen: een tweede poging levert precies dezelfde onleesbare meldingen.
  if (getoond === 0) return 'intake.review.submitBar.error';
  return sleutel;
}

function veldSleutel(field: string): string {
  return `intake.submit.measurement.${field}`;
}
